// "Moje výhry" is the one page that exists purely to feel good, so the numbers
// behind it live here: medal tiers, the tally, a personal best and a streak.
//
// Thresholds are deliberately generous — most people land in the 20-60 band, and
// a shelf where almost everything is grey would defeat the point.

// Local calendar day as YYYY-MM-DD. Deliberately NOT toISOString(), which is UTC
// and would roll the day over at the wrong moment for players east of Greenwich —
// the game's periods are local days.
export function todayIso(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export const MEDALS = ["gold", "silver", "bronze", "plain"];

export function medalFor(attempts) {
  const n = Number(attempts);
  if (!Number.isFinite(n) || n <= 0) return "plain";
  if (n < 20) return "gold";
  if (n < 40) return "silver";
  if (n < 60) return "bronze";
  return "plain";
}

// Days that neither break a streak nor add to it — outages when nobody could
// play. 2026-10-01: Google suspended the project at 12:40, the game was down until
// 4:48 the next morning. 2026-10-09..12: suspended again from Friday afternoon
// until Monday ~15:45. A win on such a day counts like any other. Same list as
// backend results.ODPUSTENE_DNY, which serves the streak numbers on this page.
export const ODPUSTENE_DNY = new Set(["2026-10-01", "2026-10-09", "2026-10-10", "2026-10-11", "2026-10-12"]);

// Consecutive solved days ending at the most recent win. A streak only counts as
// live if that win is today or yesterday — miss two days and you start over.
// `today` is injected so this stays a pure function (and testable across midnight).
export function currentStreak(dailyPeriods, today) {
  if (!dailyPeriods.length) return 0;
  const won = new Set(dailyPeriods);
  const iso = (t) => new Date(t).toISOString().slice(0, 10);
  const [y, m, d] = today.split("-").map(Number);
  const DAY = 86400000;
  let t = Date.UTC(y, m - 1, d);
  if (!won.has(iso(t))) t -= DAY; // today still open: count up to yesterday
  while (!won.has(iso(t)) && ODPUSTENE_DNY.has(iso(t))) t -= DAY;

  let streak = 0;
  for (;;) {
    if (won.has(iso(t))) streak++;
    else if (!ODPUSTENE_DNY.has(iso(t))) return streak;
    t -= DAY;
  }
}

// One pass over the history for everything the summary strip shows.
export function winsSummary(history, today) {
  const wins = (history || []).filter((r) => r && r.status === "guessed");
  const tally = { gold: 0, silver: 0, bronze: 0, plain: 0 };
  let best = null;

  wins.forEach((w) => {
    tally[medalFor(w.attempts)]++;
    const n = Number(w.attempts);
    if (Number.isFinite(n) && n > 0 && (best === null || n < Number(best.attempts))) best = w;
  });

  // Only the game's own daily word counts here. A group round is also a "win",
  // but its period is a round id, not a date — it belongs to no calendar day.
  const dailyPeriods = wins.filter((w) => w.type === "daily" && w.period).map((w) => w.period);

  return {
    total: wins.length,
    tally,
    best,
    streak: currentStreak(dailyPeriods, today),
  };
}

// Group wins into calendar months, newest first, preserving the order they came
// in. Daily rows carry the month in their period ("2026-07-15"); a mega period is
// an ISO week ("2026-W29") with no month in it, so those fall back to the day the
// win was recorded.
export function groupByMonth(wins) {
  const monthOf = (row) => {
    if (row.type !== "mega" && typeof row.period === "string" && /^\d{4}-\d{2}/.test(row.period)) {
      return row.period.slice(0, 7);
    }
    const d = row.guessed_at ? new Date(row.guessed_at) : null;
    if (!d || Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const groups = [];
  const byKey = new Map();
  (wins || []).forEach((row) => {
    const key = monthOf(row);
    if (!byKey.has(key)) {
      const group = { key, rows: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    byKey.get(key).rows.push(row);
  });
  return groups;
}

// A day-by-day timeline for the last `days` days up to `today` (YYYY-MM-DD), for
// the streak heatmap. Each cell: { date, outcome } where outcome is
// "won" (daily solved), "lost" (played, not solved) or "none" (not played).
// Dates are generated in UTC so the sequence of YYYY-MM-DD labels is stable
// regardless of the viewer's timezone.
export function winTimeline(history, today, days = 84) {
  const daily = (history || []).filter((r) => r && r.type === "daily" && r.period);
  const won = new Set(daily.filter((r) => r.status === "guessed").map((r) => r.period));
  const lost = new Set(daily.filter((r) => r.status === "in_progress").map((r) => r.period));
  const end = new Date((today || new Date().toISOString().slice(0, 10)) + "T00:00:00Z");
  const cells = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setUTCDate(end.getUTCDate() - i);
    const iso = d.toISOString().slice(0, 10);
    cells.push({ date: iso, outcome: won.has(iso) ? "won" : lost.has(iso) ? "lost" : "none" });
  }
  return cells; // oldest → newest
}

// Barva a VELIKOST poháru podle počtu pokusů.
//
// Do 23. 9. 2026 byl „obyčejný" pohár modrý (#7fb2d4) a stříbrný modrošedý
// (#9aa7b4) — na dvaceti šesti pixelech se od sebe nedaly odlišit. Šedá je teď
// opravdu šedá, a velikost navíc nese pořadí: zlatý největší, každý další o kus
// menší. Stupeň se tím pozná i koutkem oka a barva na to nezůstává sama.
export const TROPHY_TIERS = {
  gold: { color: "#e8a300", size: 26 },
  silver: { color: "#c3d0dc", size: 23 },
  bronze: { color: "#c17a3f", size: 20 },
  plain: { color: "#767b82", size: 17 },
};

export function trophyStyle(attempts) {
  return TROPHY_TIERS[medalFor(attempts)] || TROPHY_TIERS.plain;
}
